import * as React from 'react';
import * as S from './styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/types';
import { getLMSpecialHoursAPI, postLMSpecialHoursAPI } from './state/actions';
import { SectionWrapper } from 'common/baseComponents';
import { ReactComponent as IconSpecialHours } from 'common/images/icon-special-hours.svg';
import InputIconField, { InputIconFieldAction, InputIconFieldState } from 'components/InputIcon';
import { serviceLoading } from 'common/loaderReducer';
import { GET_LMSPECIALHOURS_API_REQUEST, POST_LMSPECIALHOURS_API_REQUEST } from 'features/Dealers/Listings/SpecialHours/state/actionTypes';
import ViewSpecialHours from 'features/Dealers/Listings/SpecialHours/ViewSpecialHours';
import EmptySpecialHours from 'features/Dealers/Listings/SpecialHours/EmptySpecialHours';
import EditSpecialHours from 'features/Dealers/Listings/SpecialHours/EditSpecialHours';
import Loader from 'components/Loader';
import { getListingSyncErrors } from 'features/Dealers/Listings/state/actions';
import { getDealerListingsWithErrors } from 'features/Dealers/Listings/state/actions';
import { getSyncError } from 'features/Dealers/Listings/utils';
import * as _ from 'lodash';
import { SpecialHour } from 'features/Dealers/Listings/SpecialHours/state/types';
import { regexHourFormat } from 'features/Dealers/Listings/Data/state/utils';

export interface SpecialHourFormat extends SpecialHour {
  format?: {
    month: string;
    day: string;
    year: string;
  }
}

const SpecialHours: React.FC = () => {
  const dispatch = useDispatch();
  const [firstLoad, setFirstLoad] = React.useState(true)

  const [editingState, setEditingState] = React.useState(InputIconFieldState.VIEW);
  const [specialHoursState, setSpecialHoursState] = React.useState<SpecialHourFormat[]>([]);

  const {
    isLoading,
    specialHoursData,
    isSaving,
    dealerId,
    listingSyncErrors,
    selectedDealerId,
    selectedListingId,
  } = useSelector((rootState: RootState) => {
    return {
      isLoading: serviceLoading(rootState, [GET_LMSPECIALHOURS_API_REQUEST]),
      isSaving: serviceLoading(rootState, [POST_LMSPECIALHOURS_API_REQUEST]),
      specialHoursData: rootState.dealers.listing.currentListing.specialHoursData.specialHoursData,
      dealerId: rootState.dealers.dealer.selectedDealer?.id,
      listingSyncErrors: rootState.dealers.listing.listings.listingSyncErrors,
      selectedDealerId: rootState.dealers.dealer.selectedDealer?.id,
      selectedListingId: rootState.dealers.listing.listings.selectedListingId,
    }
  }, shallowEqual);

  const setSpecialHoursFromSelector = () => {
    if (specialHoursData?.specialHours?.length) setSpecialHoursState(_.cloneDeep(specialHoursData.specialHours));
    else setSpecialHoursState([]);
  }

  React.useEffect(() => {
    if (selectedListingId && selectedListingId != "") {
      dispatch(getLMSpecialHoursAPI(selectedListingId));
    }
  }, [selectedListingId]);

  React.useEffect(() => {
    setSpecialHoursFromSelector();
  }, [JSON.stringify(specialHoursData?.specialHours)]);

  React.useEffect(() => {
    if (!firstLoad && !isSaving && selectedListingId && selectedListingId != "" && selectedDealerId) {
      dispatch(getListingSyncErrors(selectedListingId ?? ""));
      dispatch(getDealerListingsWithErrors(selectedDealerId ?? ""));
    }
    setFirstLoad(false);
  }, [isSaving])

  if (isLoading || !selectedListingId || selectedListingId == "") {
    return <Loader />;
  }

  const onFieldActionClick = (action: InputIconFieldAction) => {
    switch (action) {
      case InputIconFieldAction.SAVE:
        {
          setEditingState(InputIconFieldState.VIEW);
          let newSpecialHoursState = [...specialHoursState];
          newSpecialHoursState.map((specialHour, index) => {
            const emptyHours = specialHour.is24hrs || specialHour.isClosed;
            if (specialHour.format) {
              try {
                specialHour.startDate = new Date(parseInt(specialHour.format.year), parseInt(specialHour.format.month) - 1, parseInt(specialHour.format.day)).toISOString();
                specialHour.endDate = specialHour.startDate;
              } catch {
                specialHour.startDate = new Date().toISOString();
                specialHour.endDate = specialHour.startDate;
              }
              delete specialHour.format;
            }
            const openTime = regexHourFormat(specialHour.openTime);
            const closeTime = regexHourFormat(specialHour.closeTime);
            specialHour.openTime = openTime !== undefined ? openTime : emptyHours ? "" : "00:00";
            specialHour.closeTime = closeTime !== undefined ? closeTime : emptyHours ? "" : "23:59";
          })
          dispatch(postLMSpecialHoursAPI(selectedListingId ?? "", { specialHours: [...newSpecialHoursState] }));
          break;
        }
      case InputIconFieldAction.EDIT:
        {
          setEditingState(InputIconFieldState.EDIT);
          break;
        }
      case InputIconFieldAction.CANCEL:
        {
          setEditingState(InputIconFieldState.VIEW);
          setSpecialHoursFromSelector();
        }
    }
  }

  const renderSpecialHours = () => {

    if (editingState === InputIconFieldState.VIEW || editingState === InputIconFieldState.LOADING) {
      if (specialHoursData?.specialHours?.length) {
        return (
          <ViewSpecialHours
            specialHoursState={specialHoursState}
          />
        )
      } else {
        return <EmptySpecialHours />
      }
    } else if (editingState === InputIconFieldState.EDIT) {
      return (
        <EditSpecialHours
          specialHoursState={specialHoursState}
          setSpecialHoursState={setSpecialHoursState}
        />
      )
    }
  }

  return (
    <S.GeneralContainer>
      <SectionWrapper>
        <InputIconField
          label={'Special Hours'}
          state={isSaving ? InputIconFieldState.LOADING : editingState}
          renderValue={renderSpecialHours}
          renderIcon={() => { return (<S.IconContainer><IconSpecialHours /></S.IconContainer>) }}
          showAlertIcon={getSyncError('specialHours', listingSyncErrors) ? true : false}
          alertToolTip={getSyncError('specialHours', listingSyncErrors)}
          showEditErrorMessage={getSyncError('specialHours', listingSyncErrors) ? true : false}
          editErrorMessage={getSyncError('specialHours', listingSyncErrors)}
          onActionClick={(action: InputIconFieldAction) => { onFieldActionClick(action) }}
          disabled={false}
          disableBackground={true}
        />
      </SectionWrapper>
    </S.GeneralContainer>
  )
}

export default SpecialHours;

